import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../api';
import BaseLayout from './BaseLayout';
import FlameLoader from './FlameLoader';
import { useAuth } from '../context/AuthContext';
import '../App.css';

const FriendsPage = () => {
  const { authData } = useAuth();
  const navigate = useNavigate();
  const token = authData?.token;
  const currentUserId = authData?.user?.id;

  // ── Friends list ─────────────────────────────────────────────────────────
  const [friends, setFriends] = useState([]);
  const [loadingFriends, setLoadingFriends] = useState(true);

  // ── Outgoing pending requests ─────────────────────────────────────────────
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loadingPending, setLoadingPending] = useState(true);

  // ── Search ───────────────────────────────────────────────────────────────
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [requestSent, setRequestSent] = useState({}); // userId → 'sent' | 'error'

  // ── Remove confirmation ──────────────────────────────────────────────────
  const [confirmRemove, setConfirmRemove] = useState(null); // friend object | null

  const searchTimer = useRef(null);

  // ── Fetch friends ────────────────────────────────────────────────────────
  const fetchFriends = useCallback(async () => {
    setLoadingFriends(true);
    try {
      const res = await axios.get(`${API_BASE}/user/friends`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFriends(res.data.friends || []);
    } catch (err) {
      console.error('Error fetching friends:', err);
    } finally {
      setLoadingFriends(false);
    }
  }, [token]);

  // ── Fetch outgoing pending requests ──────────────────────────────────────
  const fetchPendingRequests = useCallback(async () => {
    setLoadingPending(true);
    try {
      const res = await axios.get(`${API_BASE}/user/outgoing-requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPendingRequests(res.data.requests || []);
    } catch (err) {
      console.error('Error fetching pending requests:', err);
    } finally {
      setLoadingPending(false);
    }
  }, [token]);

  useEffect(() => {
    document.body.classList.add('account-page-bg');
    fetchFriends();
    fetchPendingRequests();
    return () => document.body.classList.remove('account-page-bg');
  }, [fetchFriends, fetchPendingRequests]);

  // ── Live member search ───────────────────────────────────────────────────
  const runSearch = useCallback(async (q) => {
    if (!q.trim()) { setSearchResults([]); return; }
    setSearchLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/data/search`, { params: { q } });
      const members = (res.data?.members || []).filter(m => m.username !== authData?.user?.username);
      setSearchResults(members);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [authData?.user?.username]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => runSearch(val), 300);
  };

  // ── Send friend request ──────────────────────────────────────────────────
  const handleSendRequest = async (member) => {
    try {
      await axios.post(`${API_BASE}/user/send-friend-request`, {
        sender_id: currentUserId,
        recipient_id: member.id,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setRequestSent(prev => ({ ...prev, [member.id]: 'sent' }));
      fetchPendingRequests();
    } catch (err) {
      const msg = err.response?.data?.error || '';
      setRequestSent(prev => ({ ...prev, [member.id]: msg.includes('Already') ? 'already' : 'error' }));
    }
  };

  // ── Cancel outgoing request ───────────────────────────────────────────────
  const handleCancelRequest = async (req) => {
    try {
      await axios.post(`${API_BASE}/user/cancel-friend-request/${req.recipient.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPendingRequests(prev => prev.filter(r => r.request_id !== req.request_id));
      // Also clear any requestSent badge so the Add button reappears in search
      setRequestSent(prev => {
        const next = { ...prev };
        delete next[req.recipient.id];
        return next;
      });
    } catch (err) {
      console.error('Error cancelling request:', err);
    }
  };

  // Derived sets for quick lookup
  const friendIds  = new Set(friends.map(f => f.id));
  const pendingIds = new Set(pendingRequests.map(r => r.recipient.id));

  // ── Remove friend ────────────────────────────────────────────────────────
  const handleRemove = async (friend) => {
    try {
      await axios.post(`${API_BASE}/user/remove-friend/${currentUserId}/${friend.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFriends(prev => prev.filter(f => f.id !== friend.id));
    } catch (err) {
      console.error('Error removing friend:', err);
    } finally {
      setConfirmRemove(null);
    }
  };

  return (
    <BaseLayout>
      <div className="fp-page-container">

        {/* ── My Friends ─────────────────────────────────────────────── */}
        <div className="fp-page-header">
          <h1 className="fp-page-title">Friends</h1>
          <span className="fp-page-count">{loadingFriends ? '…' : friends.length}</span>
        </div>

        {loadingFriends ? (
          <FlameLoader text="Loading friends..." />
        ) : friends.length === 0 ? (
          <p className="fp-empty">You haven't added any friends yet. Search for players below.</p>
        ) : (
          <div className="fp-friends-list">
            {friends.map(f => (
              <div key={f.id} className="fp-friend-card">
                <img
                  src={f.profile_picture || '/default.jpg'}
                  alt={f.username}
                  className="fp-avatar"
                  onError={e => { e.target.src = '/default.jpg'; }}
                  onClick={() => navigate(`/user/${f.username}`)}
                  style={{ cursor: 'pointer' }}
                />
                <div
                  className="fp-friend-info"
                  onClick={() => navigate(`/user/${f.username}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="fp-friend-name">{f.firstName} {f.lastName}</span>
                  <span className="fp-friend-username">@{f.username}</span>
                </div>
                <div className="fp-friend-stats">
                  <span className="fp-stat-win">{f.score}W</span>
                  <span className="fp-stat-sep">/</span>
                  <span className="fp-stat-loss">{f.losses}L</span>
                </div>
                <button
                  className="fp-remove-btn"
                  onClick={() => setConfirmRemove(f)}
                  title="Remove friend"
                >
                  <i className="fa fa-user-times"></i>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* ── Remove confirmation ─────────────────────────────────────── */}
        {confirmRemove && (
          <div className="fp-modal-backdrop" onClick={() => setConfirmRemove(null)}>
            <div className="fp-confirm-modal" onClick={e => e.stopPropagation()}>
              <p className="fp-confirm-msg">
                Remove <strong>@{confirmRemove.username}</strong> from your friends?
              </p>
              <div className="fp-confirm-actions">
                <button className="fp-btn fp-btn-danger" onClick={() => handleRemove(confirmRemove)}>
                  Remove
                </button>
                <button className="fp-btn fp-btn-secondary" onClick={() => setConfirmRemove(null)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Outgoing Pending Requests ───────────────────────────────── */}
        {!loadingPending && pendingRequests.length > 0 && (
          <div className="fp-add-section">
            <h2 className="fp-add-heading">Pending Requests
              <span className="fp-page-count" style={{ marginLeft: 8 }}>{pendingRequests.length}</span>
            </h2>
            <div className="fp-friends-list">
              {pendingRequests.map(req => (
                <div key={req.request_id} className="fp-friend-card">
                  <img
                    src={req.recipient.profile_picture || '/default.jpg'}
                    alt={req.recipient.username}
                    className="fp-avatar"
                    onError={e => { e.target.src = '/default.jpg'; }}
                    onClick={() => navigate(`/user/${req.recipient.username}`)}
                    style={{ cursor: 'pointer' }}
                  />
                  <div
                    className="fp-friend-info"
                    onClick={() => navigate(`/user/${req.recipient.username}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="fp-friend-name">{req.recipient.firstName} {req.recipient.lastName}</span>
                    <span className="fp-friend-username">@{req.recipient.username}</span>
                  </div>
                  <span className="fp-status fp-status-sent" style={{ marginRight: 8 }}>Pending</span>
                  <button
                    className="fp-remove-btn"
                    onClick={() => handleCancelRequest(req)}
                    title="Cancel request"
                  >
                    <i className="fa fa-times"></i>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Add Friends Search ──────────────────────────────────────── */}
        <div className="fp-add-section">
          <h2 className="fp-add-heading">Add Friends</h2>
          <div className="fp-search-bar-wrap">
            <i className="fa fa-search fp-search-icon"></i>
            <input
              className="fp-search-bar"
              type="text"
              placeholder="Search players by name or username..."
              value={query}
              onChange={handleSearchChange}
              autoComplete="off"
            />
          </div>

          {searchLoading && <FlameLoader text="Searching..." />}

          {!searchLoading && query.trim() && searchResults.length === 0 && (
            <p className="fp-empty">No players found for "{query}".</p>
          )}

          {!searchLoading && searchResults.length > 0 && (
            <div className="fp-search-results">
              {searchResults.map(m => {
                const alreadyFriend = friendIds.has(m.id);
                const status = requestSent[m.id];
                return (
                  <div key={m.username} className="fp-result-row">
                    <img
                      src={m.profile_picture || '/default.jpg'}
                      alt={m.username}
                      className="fp-avatar fp-avatar-sm"
                      onError={e => { e.target.src = '/default.jpg'; }}
                      onClick={() => navigate(`/user/${m.username}`)}
                      style={{ cursor: 'pointer' }}
                    />
                    <div
                      className="fp-friend-info"
                      onClick={() => navigate(`/user/${m.username}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="fp-friend-name">{m.firstName} {m.lastName}</span>
                      <span className="fp-friend-username">@{m.username}</span>
                    </div>
                    <div className="fp-friend-stats">
                      <span className="fp-stat-win">{m.wins}W</span>
                      <span className="fp-stat-sep">/</span>
                      <span className="fp-stat-loss">{m.losses}L</span>
                    </div>
                    <div className="fp-result-action">
                      {alreadyFriend || status === 'already' ? (
                        <span className="fp-status fp-status-friend"><i className="fa fa-check"></i> Friends</span>
                      ) : pendingIds.has(m.id) || status === 'sent' ? (
                        <span className="fp-status fp-status-sent"><i className="fa fa-clock-o"></i> Requested</span>
                      ) : status === 'error' ? (
                        <span className="fp-status fp-status-error">Error</span>
                      ) : (
                        <button className="fp-btn fp-btn-add" onClick={() => handleSendRequest(m)}>
                          <i className="fa fa-user-plus"></i> Add
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </BaseLayout>
  );
};

export default FriendsPage;
